import { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, AlertCircle, Loader2, Link2, Power, RefreshCw } from 'lucide-react';
import { useSubscriptionStatus } from '../../hooks/useSubscriptionStatus';
import '../../styles/SubscriptionStatusWidget.css';

const toFa = (s) => String(s).replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);

export default function SubscriptionStatusWidget() {
  const { status, loading, error, refresh, connect, poll, disconnect } = useSubscriptionStatus();
  const [device, setDevice] = useState(null); // { device_auth_id, user_code, verification_url }
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const onConnect = async () => {
    setBusy(true); setMsg(null);
    try { setDevice(await connect()); }
    catch (e) { setMsg({ kind: 'error', text: e.message }); }
    finally { setBusy(false); }
  };

  const onCheck = async () => {
    if (!device) return;
    setBusy(true); setMsg(null);
    try {
      const r = await poll({ deviceAuthId: device.device_auth_id, userCode: device.user_code });
      if (r.status === 'connected') {
        setDevice(null);
        setMsg({ kind: 'ok', text: 'اشتراک با موفقیت متصل شد' });
        await refresh();
      } else {
        setMsg({ kind: 'error', text: 'هنوز تأیید نشده - بعد از ورود در مرورگر دوباره بزنید' });
      }
    } catch (e) { setMsg({ kind: 'error', text: e.message }); }
    finally { setBusy(false); }
  };

  const onDisconnect = async () => {
    setBusy(true); setMsg(null);
    try { await disconnect(); setMsg({ kind: 'ok', text: 'اشتراک قطع شد' }); }
    catch (e) { setMsg({ kind: 'error', text: e.message }); }
    finally { setBusy(false); }
  };

  if (loading) {
    return <div className="sub-widget" dir="rtl"><Loader2 className="spin" size={16} /> در حال بارگذاری…</div>;
  }

  const connected = Boolean(status?.connected);

  return (
    <div className="sub-widget" dir="rtl">
      <div className="sub-head">
        <div className={`sub-status ${connected ? 'sub-on' : 'sub-off'}`}>
          {connected ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          <span>{connected ? 'اشتراک ChatGPT متصل است' : 'اشتراک ChatGPT متصل نیست'}</span>
        </div>
        <button type="button" className="sub-icon-btn" onClick={refresh} title="به‌روزرسانی وضعیت">
          <RefreshCw size={14} />
        </button>
      </div>

      {connected && (
        <div className="sub-meta">
          {status.account_id && <span>حساب: {String(status.account_id).slice(0, 8)}…</span>}
          {status.expires_at && <span>انقضای توکن: {toFa(String(status.expires_at).slice(0, 10))}</span>}
        </div>
      )}

      {error && <div className="sub-msg sub-msg-error"><AlertCircle size={13} /> <span>{String(error)}</span></div>}

      {device ? (
        <div className="sub-connect-box">
          <p className="sub-connect-hint">۱) با VPN این لینک را باز کنید و وارد اکانت ChatGPT شوید:</p>
          <a className="sub-link" href={device.verification_url} target="_blank" rel="noreferrer">
            <Link2 size={13} /> {device.verification_url}
          </a>
          <p className="sub-connect-hint">۲) این کد را وارد کنید:</p>
          <div className="sub-code">{device.user_code}</div>
          <motion.button
            type="button"
            className="sub-btn sub-btn-primary"
            onClick={onCheck}
            disabled={busy}
            whileTap={!busy ? { scale: 0.98 } : undefined}
          >
            {busy ? <Loader2 size={14} className="spin" /> : <CheckCircle2 size={14} />} تأیید کردم، بررسی کن
          </motion.button>
        </div>
      ) : (
        <div className="sub-actions">
          {!connected ? (
            <motion.button
              type="button"
              className="sub-btn sub-btn-primary"
              onClick={onConnect}
              disabled={busy}
              whileTap={!busy ? { scale: 0.98 } : undefined}
            >
              {busy ? <Loader2 size={14} className="spin" /> : <Link2 size={14} />} اتصال اشتراک ChatGPT
            </motion.button>
          ) : (
            <motion.button
              type="button"
              className="sub-btn sub-btn-danger"
              onClick={onDisconnect}
              disabled={busy}
              whileTap={!busy ? { scale: 0.98 } : undefined}
            >
              {busy ? <Loader2 size={14} className="spin" /> : <Power size={14} />} قطع اتصال
            </motion.button>
          )}
        </div>
      )}

      {msg && (
        <div className={`sub-msg sub-msg-${msg.kind}`}>
          {msg.kind === 'ok' ? <CheckCircle2 size={13} /> : <AlertCircle size={13} />} <span>{msg.text}</span>
        </div>
      )}
    </div>
  );
}
